/* eslint-disable jsx-a11y/anchor-is-valid -- notifications mock uses placeholder # links */
import {refreshToken, formatBytes, useFetchHistory, isJobInProgress, useBlogPosts, useStorageUsage, useBalance, storageUsedPercent} from '@/shared/utils'
import {FC, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {PageTitle} from '../../ui/layout/core'
import {useCognitoAuth} from '../modules/auth'
import {RechargeDialog} from '../modules/purchases/RechargeDialog'
import {useSubscription} from '@/shared/utils'
import {useLayoutPanels} from '@/shared/context/ChatContext'
import moment from 'moment'

/** Same destinations linked from https://www.biobam.com/omicsbox-subscription/ (OmicsBox buy flow). */
const OMICSBOX_DOWNLOAD_PAGE = 'https://www.biobam.com/download-omicsbox/'
const OMICSBOX_DOWNLOAD_ALT = 'https://www.biobam.com/download/'
const OMICSBOX_RESOURCES = 'https://www.biobam.com/resources/'
const OMICSBOX_SUBSCRIPTION_PAGE = 'https://www.biobam.com/omicsbox-subscription/'
const OMICSBOX_TRIAL_PAGE = 'https://www.biobam.com/trial/'

/**
 * Direct installer links — must stay in sync with the `UPDATE_CONTENT` block on
 * https://www.biobam.com/download-omicsbox/ (version bumps update filenames).
 */
const OMICSBOX_INSTALLER_BASE = 'https://resources.biobam.com/omicsbox/prod/installers/'
const OMICSBOX_INSTALLER_FILES = {
  win64: 'OmicsBox_windows-x64_4_0_41.exe',
  mac: 'OmicsBox_macos_4_0_41.dmg',
  macarm: 'OmicsBox_macos_aarch64_4_0_41.dmg',
  lin64: 'OmicsBox_unix_4_0_41.sh',
} as const

type OmicsBoxInstallerKey = keyof typeof OMICSBOX_INSTALLER_FILES

function omicsBoxInstallerHref(key: OmicsBoxInstallerKey): string {
  return `${OMICSBOX_INSTALLER_BASE}${OMICSBOX_INSTALLER_FILES[key]}`
}

const OMICSBOX_DOWNLOAD_BUTTONS: {key: OmicsBoxInstallerKey; label: string; icon: string}[] = [
  {key: 'win64', label: 'Windows 64 bit (.exe)', icon: 'fab fa-windows'},
  {key: 'mac', label: 'macOS Intel (.dmg)', icon: 'fab fa-apple'},
  {key: 'macarm', label: 'macOS ARM (.dmg)', icon: 'fab fa-apple'},
  {key: 'lin64', label: 'Linux 64 bit (.sh)', icon: 'fab fa-linux'},
]

/** Same heuristic as the download page (WebGL), see filterOS() on biobam.com. */
function isAppleSiliconBrowser(): boolean {
  try {
    const w = document.createElement('canvas').getContext('webgl')
    if (w == null) {
      return false
    }
    const d = w.getExtension('WEBGL_debug_renderer_info')
    if (d) {
      const g = String(
        w.getParameter((d as {UNMASKED_RENDERER_WEBGL: number}).UNMASKED_RENDERER_WEBGL) || '',
      )
      if (g.match(/Apple/) && !g.match(/Apple GPU/)) {
        return true
      }
    }
    if (w.getSupportedExtensions()?.includes('WEBGL_compressed_texture_s3tc_srgb')) {
      return true
    }
  } catch {
    return false
  }
  return false
}

/**
 * Mirrors https://www.biobam.com/download-omicsbox/ `filterOS()` (show one relevant build).
 * Returns `other` when the OS cannot be matched — then we show all platform buttons.
 */
function detectOmicsBoxPlatform(): OmicsBoxInstallerKey | 'other' {
  if (typeof navigator === 'undefined') {
    return 'other'
  }
  const ua = navigator.userAgent
  if (ua.indexOf('Mac') !== -1) {
    if (typeof document === 'undefined') {
      return 'other'
    }
    return isAppleSiliconBrowser() ? 'macarm' : 'mac'
  }
  if (ua.indexOf('WOW64') !== -1 || ua.indexOf('Win64') !== -1) {
    return 'win64'
  }
  if (ua.indexOf('Win32') !== -1) {
    return 'win64'
  }
  if (ua.indexOf('Linux x86_64') !== -1) {
    return 'lin64'
  }
  return 'other'
}

const DashboardPage: FC = () => {
  const {currentUser} = useCognitoAuth()
  const {openFilesPanel, openChat} = useLayoutPanels()
  const {
    subscriptionId,
    hasSubscription,
    isLoading: subscriptionLoading,
    role,
    canViewAiChat,
  } = useSubscription()
  const [showRecharge, setShowRecharge] = useState(false)
  /** After login: no cloud subscription id on the account (same signal as `hasSubscription` in context). */
  const showNoSubscriptionPromos = !subscriptionLoading && !hasSubscription

  /** Shown download button(s) — same OS filter as https://www.biobam.com/download-omicsbox/ */
  const [omicsBoxPlatform] = useState<OmicsBoxInstallerKey | 'other'>(() => detectOmicsBoxPlatform())

  // Set up date filtering for the latest 10 days
  const todayDate = moment().format('yyyy-MM-DD')
  const tenDaysBackDate = moment().subtract(10, 'days').format('yyyy-MM-DD')
  
  const filteredData = {
    startCreationTime: new Date(`${tenDaysBackDate}T00:00:00`).getTime(),
    endCreationTime: new Date(`${todayDate}T23:59:59`).getTime(),
  }
  
  // Fetch history data using the same pattern as TableHistoryAg
  const historyQuery = useFetchHistory(filteredData)
  const usageQuery = useStorageUsage()
  const balanceQuery = useBalance(subscriptionId)
  const usage = usageQuery.data ?? null
  const balance = balanceQuery.data ?? null

  useEffect(() => {
    const refreshTokenInterval = setInterval(refreshToken, 1000 * 50 * 60)
    return () => {
      clearInterval(refreshTokenInterval)
    }
  }, [])

  // Share of the storage limit in use (see storageLimit.ts)
  const calculateStoragePercentage = () => (usage === null ? 0 : Math.round(storageUsedPercent(usage)))

  // Get storage percentage for display (includes '--' for loading)
  const getStoragePercentageDisplay = () => {
    if (usage === null) return '--'
    return calculateStoragePercentage()
  }

  // Process history data to extract running jobs information
  const runningJobs = historyQuery.data ? historyQuery.data
    .filter((job) => isJobInProgress(job))
    .slice(0, 4) // Limit to first 4 jobs
    .map((job) => {
      // Calculate duration from CreationTime to current time (for running jobs) or lastStopTime (for completed jobs)
      const creationTime = new Date(job.CreationTime)
      const currentTime = job.lastStopTime ? new Date(job.lastStopTime) : new Date()
      const durationMs = currentTime.getTime() - creationTime.getTime()
      
      // Format duration similar to TableHistoryAg
      const durationDays = Math.floor(durationMs / (1000 * 60 * 60 * 24))
      const durationHours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))
      
      const duration = `${durationDays > 0 ? `${durationDays}d ` : ''}${
        durationHours > 0 ? `${durationHours}h ` : ''
      }${durationMinutes}m`
      
      // Format input size (check if inputDataSize exists in the job data)
      const inputSize = (job as any).inputDataSize ? formatBytes((job as any).inputDataSize) : '--'
      
      return {
        id: job.customId || '--',
        name: job.serviceName || 'Unknown Task',
        duration: duration || '--',
        inputSize: inputSize,
      }
    }) : []

  // Mock notifications
  const notifications = [
    'Job "JD82JD" finished successfully',
    'Your cloud balance is now below one million, please consider to recharge here',
    'Differential Expression Analysis completed for dataset "Sample_A"',
    'New version of InterProScan available',
  ]

  // Popular OmicsBox tools
  const popularTools = [
    {name: 'BLAST', description: 'Sequence Similarity Search', icon: 'fas fa-search'},
    {name: 'Gene Ontology', description: 'Functional Annotation', icon: 'fas fa-tags'},
    {name: 'RNA-Seq Analysis', description: 'Transcriptome Analysis', icon: 'fas fa-dna'},
    {name: 'InterProScan', description: 'Protein Domain Analysis', icon: 'fas fa-puzzle-piece'},
    {
      name: 'Differential Expression',
      description: 'Statistical Analysis',
      icon: 'fas fa-chart-line',
    },
    {name: 'Pathway Analysis', description: 'Biological Pathways', icon: 'fas fa-project-diagram'},
    {name: 'GSEA', description: 'Gene Set Enrichment', icon: 'fas fa-layer-group'},
    {name: 'Functional Analysis', description: 'GO Enrichment', icon: 'fas fa-microscope'},
  ]

  // Feature flags to hide sections temporarily
  const SHOW_NOTIFICATIONS = false
  const SHOW_TOOLS = false

  // Fetch enough posts for 6×2 on wide screens; CSS shows 3 / 6 / 9 / 12.
  const blogPostsQuery = useBlogPosts(12)

  return (
    <>
      {showRecharge && (
        <RechargeDialog
          closeModal={() => setShowRecharge(false)}
          role={role}
          subscriptionId={subscriptionId}
        />
      )}
      <div className='app-content flex-column-fluid'>
        {showNoSubscriptionPromos ? (
          /* No cloud subscription on the account: the promos replace the stat cards entirely. */
          <div className='ob-dashboard-cards-host mb-5'>
            <div className='ob-dashboard-promo-grid'>
              <div className='card ob-dashboard-card'>
                <div className='card-header'>
                  <h4 className='card-title mb-0'>
                    <i className='fas fa-download me-2'></i>
                    Download OmicsBox
                  </h4>
                </div>
                <div className='card-body'>
                  <p className='ob-dashboard-card-desc mb-3'>
                    Get the latest OmicsBox installer for your system. The same builds and OS detection as on our
                    download page.
                  </p>
                  <div className='d-flex flex-wrap gap-2 mb-3'>
                    {OMICSBOX_DOWNLOAD_BUTTONS.filter(
                      (b) => omicsBoxPlatform === 'other' || b.key === omicsBoxPlatform,
                    ).map((b) => (
                      <a
                        key={b.key}
                        href={omicsBoxInstallerHref(b.key)}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='btn btn-primary btn-sm'
                      >
                        <i className={`${b.icon} me-2`} aria-hidden />
                        {b.label}
                      </a>
                    ))}
                  </div>
                  <div className='ob-dashboard-card-note ob-dashboard-card-actions'>
                    <a href={OMICSBOX_DOWNLOAD_PAGE} target='_blank' rel='noopener noreferrer'>
                      All platforms &amp; previous versions
                      <i className='fas fa-external-link-alt ms-1 small'></i>
                    </a>
                    <span className='px-1'>·</span>
                    <a href={OMICSBOX_DOWNLOAD_ALT} target='_blank' rel='noopener noreferrer'>
                      Download overview
                      <i className='fas fa-external-link-alt ms-1 small'></i>
                    </a>
                    <span className='px-1'>·</span>
                    <a href={OMICSBOX_RESOURCES} target='_blank' rel='noopener noreferrer'>
                      Resources
                      <i className='fas fa-external-link-alt ms-1 small'></i>
                    </a>
                    <span className='px-1'>·</span>
                    <a href={OMICSBOX_SUBSCRIPTION_PAGE} target='_blank' rel='noopener noreferrer'>
                      Pricing &amp; purchase
                      <i className='fas fa-external-link-alt ms-1 small'></i>
                    </a>
                  </div>
                </div>
              </div>
              <div className='card ob-dashboard-card'>
                <div className='card-header'>
                  <h4 className='card-title mb-0'>
                    <i className='fas fa-key me-2'></i>
                    Request OmicsBox trial
                  </h4>
                </div>
                <div className='card-body'>
                  <p className='ob-dashboard-card-desc mb-3'>
                    Register to receive a trial license key by email and explore OmicsBox with full functionality for
                    a limited time.
                  </p>
                  <div className='ob-dashboard-card-actions'>
                    <a
                      href={OMICSBOX_TRIAL_PAGE}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='btn btn-primary btn-sm mb-2'
                    >
                      Request trial
                      <i className='fas fa-external-link-alt ms-2'></i>
                    </a>
                    <div>
                      <a
                        href={OMICSBOX_TRIAL_PAGE}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='ob-dashboard-card-note'
                      >
                        www.biobam.com/trial
                        <i className='fas fa-external-link-alt ms-1 small'></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              {/* Announcement only: without a linked subscription the chat panel can only show
                  AIChatPaywall, so this points at the buy flow instead of opening it. */}
              <div className='card ob-dashboard-card'>
                <div className='card-header'>
                  <h4 className='card-title mb-0'>
                    <i className='fas fa-robot me-2'></i>
                    OmicsAI Agent
                  </h4>
                </div>
                <div className='card-body'>
                  <p className='ob-dashboard-card-desc mb-3'>
                    Your AI assistant for bioinformatics — ask about your data, run analyses and
                    interpret results in chat. Included with an OmicsBox cloud subscription.
                  </p>
                  <div className='ob-dashboard-card-actions'>
                    <a
                      href={OMICSBOX_SUBSCRIPTION_PAGE}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='btn btn-primary btn-sm mb-2'
                    >
                      Pricing &amp; purchase
                      <i className='fas fa-external-link-alt ms-2'></i>
                    </a>
                    <div>
                      <a
                        href={OMICSBOX_RESOURCES}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='ob-dashboard-card-note'
                      >
                        Learn what OmicsBox can do
                        <i className='fas fa-external-link-alt ms-1 small'></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Stat cards reflow 1 → 2 → 4 columns on this row's own width, so the lateral panels
             narrow the content without squeezing the cards — see .ob-dashboard-stats-grid. */
          <div className='ob-dashboard-cards-host mb-5'>
            <div className='ob-dashboard-stats-grid'>
              {/* Running jobs */}
              <div className='card ob-dashboard-card'>
                <div className='card-header d-flex align-items-center justify-content-between'>
                  <h4 className='card-title mb-0'>
                    <Link to='/jobs'>
                      <i className='fas fa-play-circle me-2'></i>
                      Running Jobs
                    </Link>
                  </h4>
                  {runningJobs.length > 0 && (
                    <span className='ob-dashboard-job-count'>{runningJobs.length}</span>
                  )}
                </div>
                <div className='card-body p-0'>
                  {/* Keyed for the same reason as the blog branches below. */}
                  {historyQuery.isLoading ? (
                    <div
                      key='jobs-loading'
                      className='d-flex justify-content-center align-items-center'
                      style={{minHeight: '120px'}}
                    >
                      <div className='spinner-border text-primary' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                    </div>
                  ) : runningJobs.length > 0 ? (
                    <div key='jobs-list' className='ob-dashboard-jobs'>
                      {runningJobs.map((job) => (
                        <div key={job.id} className='ob-dashboard-job-item'>
                          <div className='ob-dashboard-job-indicator' />
                          <div className='ob-dashboard-job-info'>
                            <div className='ob-dashboard-job-name'>{job.name}</div>
                            <div className='ob-dashboard-job-meta'>
                              <span className='ob-dashboard-job-id'>{job.id}</span>
                              <span className='ob-dashboard-job-separator'>·</span>
                              <span className='ob-dashboard-job-duration'>
                                <i className='fas fa-clock me-1'></i>
                                {job.duration}
                              </span>
                              {job.inputSize !== '--' && (
                                <>
                                  <span className='ob-dashboard-job-separator'>·</span>
                                  <span>{job.inputSize}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <Link to='/jobs' className='ob-dashboard-jobs-footer mt-auto'>
                        View all jobs
                        <i className='fas fa-arrow-right ms-1'></i>
                      </Link>
                    </div>
                  ) : (
                    <div key='jobs-empty' className='ob-dashboard-jobs'>
                      <div
                        className='d-flex flex-column align-items-center justify-content-center'
                        style={{minHeight: '120px', padding: 'var(--ob-spacing-4)'}}
                      >
                        <i
                          className='fas fa-check-circle mb-2'
                          style={{fontSize: '1.5rem', color: 'var(--ob-gray-400)'}}
                        ></i>
                        <p className='ob-dashboard-card-desc'>No running jobs</p>
                      </div>
                      <Link to='/jobs' className='ob-dashboard-jobs-footer mt-auto'>
                        View all jobs
                        <i className='fas fa-arrow-right ms-1'></i>
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Used storage */}
              <div className='card ob-dashboard-card'>
                <div className='card-header'>
                  <h4 className='card-title mb-0'>
                    {/* Files is a lateral panel now, so this opens it rather than navigating. */}
                    <button
                      type='button'
                      className='btn btn-link p-0 align-baseline text-start'
                      onClick={() => openFilesPanel()}
                    >
                      <i className='fas fa-hdd me-2'></i>
                      Used Storage
                    </button>
                  </h4>
                </div>
                <div className='card-body'>
                  <div className='ob-dashboard-stat-head'>
                    <div className='ob-dashboard-stat-text'>
                      <div className='ob-dashboard-stat-value'>
                        {usage !== null ? formatBytes(usage) : '--'}
                      </div>
                      <p className='ob-dashboard-card-desc'>Your data storage usage</p>
                    </div>
                    <i className='fas fa-database ob-dashboard-stat-icon text-info' aria-hidden></i>
                  </div>
                  <div className='progress mt-3' style={{height: '8px'}}>
                    <div
                      className='progress-bar bg-info'
                      role='progressbar'
                      style={{width: `${calculateStoragePercentage()}%`}}
                      aria-valuenow={calculateStoragePercentage()}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <small className='ob-dashboard-card-note mt-1'>
                    {usage !== null
                      ? `${getStoragePercentageDisplay()}% of 5 TB limit used`
                      : '-- of 5 TB limit used'}
                  </small>
                  <div className='ob-dashboard-card-actions'>
                    <button
                      type='button'
                      className='btn btn-outline-primary btn-sm'
                      onClick={() => openFilesPanel()}
                    >
                      <i className='fas fa-folder-open me-1'></i>
                      Browse files
                    </button>
                  </div>
                </div>
              </div>

              {/* Cloud unit balance */}
              <div className='card ob-dashboard-card'>
                <div className='card-header'>
                  <h4 className='card-title mb-0'>
                    <i className='fas fa-cloud me-2'></i>
                    Cloud Unit Balance
                  </h4>
                </div>
                <div className='card-body'>
                  <div className='ob-dashboard-stat-head'>
                    <div className='ob-dashboard-stat-text'>
                      <div className='ob-dashboard-stat-value'>
                        {balance !== null ? balance.toLocaleString() : '--'}
                      </div>
                      <p className='ob-dashboard-card-desc'>Cloud computation units available</p>
                    </div>
                    <i className='fas fa-coins ob-dashboard-stat-icon text-primary' aria-hidden></i>
                  </div>
                  <div className='ob-dashboard-card-actions'>
                    <button
                      type='button'
                      className='btn btn-outline-primary btn-sm'
                      onClick={() => setShowRecharge(true)}
                    >
                      <i className='fas fa-plus me-1'></i>
                      Add Units
                    </button>
                  </div>
                </div>
              </div>

              {/* OmicsAI Agent — same subscription gate (ENGINE) as the header toggle, so the card
                  is never a dead control. */}
              {canViewAiChat && (
                <div className='card ob-dashboard-card'>
                  <div className='card-header'>
                    <h4 className='card-title mb-0'>
                      <button
                        type='button'
                        className='btn btn-link p-0 align-baseline text-start'
                        onClick={openChat}
                      >
                        <i className='fas fa-robot me-2'></i>
                        OmicsAI Agent
                      </button>
                    </h4>
                  </div>
                  <div className='card-body'>
                    <div className='ob-dashboard-stat-head'>
                      <div className='ob-dashboard-stat-text'>
                        <div className='ob-dashboard-stat-value'>New</div>
                        <p className='ob-dashboard-card-desc'>
                          Your AI assistant for bioinformatics — ask about your data, run analyses
                          and interpret results.
                        </p>
                      </div>
                      <i className='fas fa-robot ob-dashboard-stat-icon text-primary' aria-hidden></i>
                    </div>
                    <div className='ob-dashboard-card-actions'>
                      <button type='button' className='btn btn-outline-primary btn-sm' onClick={openChat}>
                        <i className='fas fa-comments me-1'></i>
                        Open chat
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Latest Notifications */}
        {SHOW_NOTIFICATIONS && (
          <div className='row g-5 mb-5'>
            <div className='col-12'>
              <div className='card'>
                <div className='card-header'>
                  <h4 className='card-title mb-0'>
                    <i className='fas fa-bell me-2'></i>
                    Latest Notifications
                  </h4>
                </div>
                <div className='card-body'>
                  <div className='timeline timeline-border-dashed'>
                    {notifications.map((notification, index) => (
                      <div key={index} className='timeline-item'>
                        <div className='timeline-line w-40px'></div>
                        <div className='timeline-icon symbol symbol-circle symbol-40px'>
                          <div className='symbol-label bg-light'>
                            <i
                              className={`fas ${
                                index === 0
                                  ? 'fa-check text-success'
                                  : index === 1
                                  ? 'fa-exclamation-triangle text-warning'
                                  : 'fa-info text-primary'
                              } fs-6`}
                            ></i>
                          </div>
                        </div>
                        <div className='timeline-content ms-3'>
                          <div className='fs-7 text-muted mb-1'>{notification}</div>
                          {index === 1 && (
                            <a href='#' className='btn btn-sm btn-light-warning'>
                              Recharge
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Tools Panel - Spans Both Columns */}
        {SHOW_TOOLS && (
          <div className='row g-5 mb-5'>
            <div className='col-12'>
              <div className='card'>
                <div className='card-header'>
                  <h4 className='card-title mb-0'>
                    <i className='fas fa-star me-2'></i>
                    Top OmicsBox Tools
                  </h4>
                </div>
                <div className='card-body'>
                  <div className='row g-3'>
                    {popularTools.map((tool, index) => (
                      <div key={index} className='col-lg-3 col-md-6'>
                        <div className='card card-flush border border-gray-300 h-100'>
                          <div className='card-body text-center p-4'>
                            <div className='mb-3'>
                              <i className={`${tool.icon} fa-3x text-primary`}></i>
                            </div>
                            <h5 className='mb-2'>{tool.name}</h5>
                            <p className='text-muted small mb-3'>{tool.description}</p>
                            <button className='btn btn-outline-primary btn-sm'>Launch Tool</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Latest Blog Posts Panel */}
        <div className='row g-5 mb-5'>
          <div className='col-12'>
            <div className='card'>
              <div className='card-header'>
                <h4 className='card-title mb-0'>
                  <i className='fas fa-newspaper me-2'></i>
                  Latest Blog Posts
                </h4>
                <div className='card-toolbar'>
                  <a
                    href='https://blog.biobam.com'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='btn btn-sm btn-outline-primary'
                  >
                    Visit Blog
                  </a>
                </div>
              </div>
              <div className='card-body'>
                {/* Keys keep these branches on separate DOM nodes. Unkeyed, React morphs the
                    loading markup into the grid — <div><div.spinner-border> becomes
                    <div.blog-grid-host><div.blog-grid> — and WebKit keeps the running
                    `spinner-border` animation on the recycled node, so the whole grid rotates. */}
                {blogPostsQuery.isLoading ? (
                  <div
                    key='blog-loading'
                    className='d-flex justify-content-center align-items-center'
                    style={{minHeight: '200px'}}
                  >
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading blog posts...</span>
                    </div>
                  </div>
                ) : blogPostsQuery.isError ? (
                  <div key='blog-error' className='alert alert-warning mb-0'>
                    <i className='fas fa-exclamation-triangle me-2'></i>
                    Unable to load blog posts. Please try again later.
                  </div>
                ) : blogPostsQuery.data && blogPostsQuery.data.length > 0 ? (
                  <div key='blog-grid' className='ob-dashboard-blog-grid-host'>
                    <div className='ob-dashboard-blog-grid'>
                    {blogPostsQuery.data.map((post) => (
                      <div key={post.id} className='card border border-gray-300 ob-dashboard-blog-card'>
                        <a
                          className='card-img-top ob-dashboard-blog-thumb'
                          href={post.link}
                          target='_blank'
                          rel='noopener noreferrer'
                          aria-label={post.title}
                        >
                          <img src={post.image} alt='' loading='lazy' />
                        </a>
                        <div className='card-body d-flex flex-column'>
                          <h6 className='card-title'>{post.title}</h6>
                          <p className='card-text text-muted small flex-grow-1'>{post.subtitle}</p>
                          <div className='mt-auto'>
                            <small className='text-muted'>
                              <i className='fas fa-calendar me-1'></i>
                              {post.date}
                            </small>
                            <div className='mt-2'>
                              <a
                                href={post.link}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='btn btn-outline-info btn-sm'
                              >
                                Read More
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                ) : (
                  <p key='blog-empty' className='text-muted mb-0'>
                    No blog posts available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Login Alert */}
        {!currentUser && (
          <div className='row g-5'>
            <div className='col-12'>
              <div className='alert alert-info'>
                <i className='fas fa-info-circle me-2'></i>
                <strong>Please log in</strong> to access your files, analysis results, and
                personalized dashboard data.
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const DashboardWrapper: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Dashboard</PageTitle>
      <DashboardPage />
    </>
  )
}

export {DashboardWrapper}
